import { formatPhoneDisplay } from '@mdvip/shared/lib/phone';
import { cn } from '@mdvip/shared/lib/utils';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import mdvipLockupColor from '@/assets/membership-card/mdvip-affiliated-color.svg';
import mdvipLockupWhite from '@/assets/membership-card/mdvip-affiliated-white.svg';
import type { MembershipCardData } from '@/hooks/membership/useMembershipCard';
import { resolveCoBrandLogo } from '@/lib/membership/coBrandLogo';

/*
 * The MDVIP membership card, recreated from the legacy Connect portal rather than designed anew —
 * a product decision, so the palette, type scale and layout here are legacy's, not the design
 * system's. This component is expected to be replaced wholesale by the design-system card;
 * docs/100-legacy-membership-card.md is the full record and the removal checklist.
 *
 * Legacy's `rem` is 10px (`html { font-size: 62.5% }`) and its card is a fixed 520 x 326px. Every
 * measurement below is that pixel value as a percentage of 520, in `cqw`, resolved against the
 * card's own container. One set of proportions then holds at rail width, at modal width, and at
 * the 85mm print footprint.
 */
const LEGACY = {
  /** container padding: 30px */
  padding: 'p-[5.77cqw]',
  /** border radius: 12px */
  radius: 'rounded-[2.31cqw]',
  /** MDVIP lockup width: 114px */
  logo: 'w-[21.92cqw]',
  /** The MDVIP lockup's resulting height, 40px — the co-brand mark is drawn to match it. */
  logoHeight: 'h-[7.69cqw]',
  /** member name: 29px */
  memberName: 'text-[5.58cqw]',
  /** card display name and the MEMBER ID line: 20px */
  displayName: 'text-[3.85cqw]',
  /** benefit lines: 19px, 19px apart */
  benefit: 'text-[3.65cqw]',
  benefitGap: 'gap-[3.65cqw]',
  /** check glyph: 28px, 12px from its label */
  check: 'size-[5.38cqw] shrink-0',
  checkGap: 'gap-[2.31cqw]',
  /** co-brand line, phone lines, additional lines: 18px */
  body: 'text-[3.46cqw]',
  /** phone block padding: 30px top and bottom */
  contactsPadding: 'py-[5.77cqw]',
} as const;

/*
 * Legacy's line-heights, which drive whether the card's content fits its fixed 326px height at
 * all — the benefits list plus the footer add up to almost exactly the available space, so these
 * are load-bearing rather than cosmetic. Unstyled text on the legacy card inherits the theme's
 * body line-height of 1.5.
 */
const LEADING = {
  benefit: 'leading-[1.1]',
  footer: 'leading-[1.2]',
  displayName: 'leading-[1.5]',
  contact: 'leading-[1.15]',
  description: 'leading-[1.1]',
} as const;

const TIER_BACKGROUND: Record<string, string> = {
  STANDARD_MEMBERSHIP: 'bg-[var(--mc-tier-standard)]',
  WOMENS_HEALTH: 'bg-[var(--mc-tier-womens-health)]',
  AWP_PLUS: 'bg-[var(--mc-tier-awp-plus)]',
};

/** Legacy gives an unrecognized tier no background at all, which renders white text on nothing. */
const TIER_FALLBACK = TIER_BACKGROUND.STANDARD_MEMBERSHIP;

/** Legacy prints these in capitals; the copy itself is sentence case and uppercased in CSS. */
const BENEFIT_KEYS = ['wellnessProgram', 'reachable', 'sameDayVisits'] as const;

/**
 * The MDVIP check glyph: a filled disc with the tick knocked out of it, so the tier color shows
 * through the checkmark. Font Awesome's `circle-check` draws a solid tick instead, so this one is
 * inlined from legacy rather than routed through the shared `Icon`.
 */
function CheckGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 13.77 13.77" aria-hidden="true" className={className}>
      <path
        fill="#ffffff"
        d="M6.89,0C3.08,0,0,3.08,0,6.89s3.08,6.89,6.89,6.89,6.89-3.08,6.89-6.89S10.69,0,6.89,0ZM6.21,11.54l-3.12-3.92,1.06-1.02,1.83,2.33,3.49-6.7,1.21.81-4.47,8.5Z"
      />
    </svg>
  );
}

/**
 * The shell both faces share: an outer element that sizes the card and declares the query
 * container, and an inner surface that draws it.
 *
 * The split is required, not cosmetic — `cqw` resolves against the nearest *ancestor* container,
 * never the element declaring one. Padding and type set directly on the container element would
 * size off the viewport instead of the card.
 */
function Face({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div data-slot="membership-card-face" className="@container aspect-[520/326] w-full">
      <div
        data-slot="membership-card-surface"
        className={cn(
          'mdvip-membership-card flex h-full w-full flex-col overflow-hidden font-light',
          LEGACY.padding,
          LEGACY.radius,
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}

interface FrontFaceProps {
  data: MembershipCardData;
  showBenefits: boolean;
}

/**
 * Legacy lays this face out as two wrapping Bootstrap flex lines — an identity/benefits row, then
 * the member footer — each sized by its own content, with the footer pinned to the bottom. The
 * identity column then wraps into two lines of its own that DO split that row's height evenly,
 * which is why the display name lands around the middle of the card rather than directly under
 * the logo: hence the 2-row grid.
 */
function FrontFace({ data, showBenefits }: FrontFaceProps) {
  const { t } = useTranslation('members');
  const tier = (data.membershipTier && TIER_BACKGROUND[data.membershipTier]) ?? TIER_FALLBACK;

  return (
    <Face className={cn('justify-between text-white', tier)}>
      {/* No gap: legacy carries Bootstrap's 20px gutter as padding inside the benefits column, so
          the identity column keeps its full 58.33% — enough for a long display name like
          "Prasanth Gogineni, MD, FACP" to stay on one line. The benefits are what give this row its
          height, so without them the row takes the slack rather than leaving a void. */}
      <div className={cn('flex items-stretch', !showBenefits && 'flex-1')}>
        <div className="grid min-w-0 basis-[58.33%] grid-rows-2">
          <img
            src={mdvipLockupWhite}
            alt={t('profile.membership.card.logoAlt')}
            className={cn(LEGACY.logo, 'h-auto self-start')}
          />
          <div className="self-start">
            <div className={cn(LEGACY.displayName, LEADING.displayName, 'font-semibold')}>{data.cardDisplayName}</div>
            <div className={cn(LEGACY.body, 'italic leading-none')}>{data.coBrandName}</div>
          </div>
        </div>
        {showBenefits && (
          <ul className={cn('flex basis-[41.67%] flex-col pl-[3.85cqw]', LEGACY.benefitGap)}>
            {BENEFIT_KEYS.map((key) => (
              <li
                key={key}
                className={cn(
                  'flex items-start font-semibold uppercase',
                  LEGACY.benefit,
                  LEADING.benefit,
                  LEGACY.checkGap,
                )}
              >
                <CheckGlyph className={LEGACY.check} />
                {t(`profile.membership.benefits.${key}`)}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className={LEADING.footer}>
        <div className={cn(LEGACY.memberName, 'font-normal')}>{data.memberName}</div>
        <div className={cn(LEGACY.displayName, 'uppercase')}>
          {t('profile.membership.card.memberIdLabel')} {data.mdvipId}
        </div>
      </div>
    </Face>
  );
}

function BackFace({ data }: { data: MembershipCardData }) {
  const { t } = useTranslation('members');
  const coBrandLogo = resolveCoBrandLogo(data.coBrandName);

  return (
    <Face className="justify-between border border-[var(--mc-border)] bg-white text-[var(--mc-ink)]">
      <div className="flex items-end">
        <img
          src={mdvipLockupColor}
          alt={t('profile.membership.card.logoAlt')}
          className={cn(LEGACY.logo, 'h-auto shrink-0')}
        />
        <div className="flex min-w-0 flex-1 justify-end">
          {coBrandLogo ? (
            /* Legacy stretches the partner mark to fill this slot, which letterboxes it to the
               lockup's height. Sizing by height instead reaches the same drawn size without the
               distortion; object-contain keeps the widest mark (Memorial, 13:1) in proportion when
               it hits the slot's width instead. */
            <img
              src={coBrandLogo}
              alt={data.coBrandName ?? ''}
              className={cn(LEGACY.logoHeight, 'w-auto max-w-full object-contain object-right')}
            />
          ) : (
            <div className={cn(LEGACY.displayName, 'truncate text-right font-semibold leading-none')}>
              {data.cardDisplayName}
            </div>
          )}
        </div>
      </div>
      {data.cardContacts.length > 0 && (
        <dl className={cn('flex flex-1 flex-col', LEGACY.contactsPadding, LEGACY.body, LEADING.contact)}>
          {data.cardContacts.map((contact) => (
            // The label column starts one gutter before the 33.33% mark: legacy zeroes its
            // padding-left while the Bootstrap row keeps its negative margin.
            <div key={`${contact.phone}-${contact.label}`} className="flex">
              <dt className="basis-[calc(33.33%-3.85cqw)] font-normal">{formatPhoneDisplay(contact.phone)}</dt>
              <dd className="flex-1">{contact.label}</dd>
            </div>
          ))}
        </dl>
      )}
      {/* Legacy sets these lines `text-wrap: nowrap` and clips the overflow rather than letting a
          long line reflow the card. */}
      <div className={cn('whitespace-nowrap font-normal', LEGACY.body, LEADING.description)}>
        <div>{data.additionalLine1}</div>
        <div>{data.additionalLine2}</div>
      </div>
    </Face>
  );
}

export interface MembershipCardArtProps {
  side: 'front' | 'back' | 'both';
  /** `small` is the Profile rail preview, where the benefit lines have no room. */
  size?: 'small' | 'full';
  data: MembershipCardData;
  className?: string;
}

/**
 * The card artwork, and only the artwork — no heading, no actions, no modal chrome. Rendered
 * unchanged at all three surfaces (rail preview, print modal, print output), which is how legacy
 * worked too: one card component, reused rather than redrawn per surface.
 */
export function MembershipCardArt({ side, size = 'full', data, className }: MembershipCardArtProps) {
  const showBenefits = size === 'full';

  if (side === 'front') {
    return (
      <div className={className}>
        <FrontFace data={data} showBenefits={showBenefits} />
      </div>
    );
  }
  if (side === 'back') {
    return (
      <div className={className}>
        <BackFace data={data} />
      </div>
    );
  }
  // The @container wrapper and the grid reading @md: off it have to be separate elements — a
  // container query can't resolve against the element that declares the container.
  return (
    <div className={cn('@container', className)}>
      <div data-slot="membership-card-faces" className="grid grid-cols-1 gap-4 @md:grid-cols-2">
        <FrontFace data={data} showBenefits={showBenefits} />
        <BackFace data={data} />
      </div>
    </div>
  );
}
